import React from 'react';
import { Box, Text, Loader, Alert, Tooltip } from '@mantine/core';
import useAuthenticatedData from '../../../../hooks/useAuthenticatedData';
import config from '../../../../config';

const RepairsDepartmentPerformancePanel = () => {
    const { data, isLoading, error } = useAuthenticatedData(`${config.apiBaseUrl}/supply-chain/repairs/department/scorecard`);

    // Map the status from the API to actual colors for background and border.
    const statusColorMapping = {
        green: { background: '#2A7040', border: '#1E5A33' },
        yellow: { background: '#A07D32', border: '#7C6025' },
        red: { background: '#944848', border: '#7A3636' },
        gray: { background: '#4F4F4F', border: '#3B3B3B' }
    };

    const formatCurrency = (value) => {
        if (value === null || value === undefined) return 'N/A';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
    };

    const formatPercent = (value) => {
        if (value === null || value === undefined) return 'N/A';
        if (isNaN(value)) return 'N/A';
        return `${Math.round(value)}%`;
    };

    const formatNumber = (value) => {
        if (value === null || value === undefined) return 'N/A';
        return new Intl.NumberFormat('en-US').format(value);
    };

    const getStatusColor = (value, target, isHigherBetter = true, thresholdFactor = 0.8) => {
        if (value === null || value === undefined) return 'gray';

        if (isHigherBetter) {
            if (value >= target) return 'green';
            if (value >= target * thresholdFactor) return 'yellow';
            return 'red';
        } else {
            if (value <= target) return 'green';
            if (value <= target * (2 - thresholdFactor)) return 'yellow';
            return 'red';
        }
    };

    const getMarginPercentStatus = (value) => {
        if (value === null || value === undefined || isNaN(value)) return 'gray';
        if (value >= 20) return 'green';
        if (value >= 15) return 'yellow';
        return 'red';
    };

    const getInventoryHealthStatus = (value, ageBucket) => {
        const goals = {
            '0-90': 56,
            '91-180': 26,
            '181-365': 16,
            '366+': 2
        };

        const goal = goals[ageBucket];

        if (ageBucket === '0-90') {
            if (value >= goal) return 'green';
            if (value >= goal * 0.85) return 'yellow';
            return 'red';
        } else {
            if (value <= goal) return 'green';
            if (value <= goal * 1.15) return 'yellow';
            return 'red';
        }
    };

    // Helper functions for handling clicks.
    const handleInventoryPlannedSpendClick = (period) => {
        window.open(`/supply-chain/repairs/department/scorecard/inventory-planned-spend-details?period=${period}`, '_blank');
    };

    const handleInventoryLandedSpendClick = (period) => {
        window.open(`/supply-chain/repairs/department/scorecard/inventory-landed-spend-details?period=${period}`, '_blank');
    };

    const handleInventoryRevenueClick = (period) => {
        window.open(`/supply-chain/repairs/department/scorecard/inventory-revenue-details?period=${period}`, '_blank');
    };
    
    const handleInventoryHealthClick = (period) => {
        window.open(`/supply-chain/repairs/department/scorecard/inventory-health-details?period=${period}`, '_blank');
    };
    
    const handleNewVendorROClick = (period) => {
        window.open(`/supply-chain/repairs/department/scorecard/new-vendor-ro-details?period=${period}`, '_blank');
    };
    
    const handleNewVendorRFQClick = (period) => {
        window.open(`/supply-chain/repairs/department/scorecard/new-vendor-rfq-details?period=${period}`, '_blank');
    };

    const CompactMetricBox = ({ value, format, status, onClick, tooltip }) => {
        const safeValue = value ?? 0;
        const hasData = safeValue !== null && !isNaN(safeValue);
        const { background, border } = statusColorMapping[status] || { background: '#4F4F4F', border: '#3B3B3B' };

        const formattedValue =
            format === 'currency'
                ? formatCurrency(hasData ? safeValue : 0)
                : format === 'percent'
                    ? formatPercent(hasData ? safeValue : 0)
                    : hasData
                        ? format === 'number'
                            ? formatNumber(safeValue)
                            : safeValue.toFixed(1)
                        : 'N/A';

        const boxContent = (
            <Box
                onClick={onClick}
                style={{
                    backgroundColor: background,
                    padding: '6px',
                    borderRadius: '4px',
                    border: `2px solid ${border}`,
                    width: '120px',
                    height: '32px',
                    textAlign: 'center',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: onClick ? 'pointer' : 'default'
                }}
            >
                <Text 
                    size="sm" 
                    fw={600} 
                    c="white"
                    style={{ fontSize: '0.85rem' }}
                >
                    {formattedValue}
                </Text>
            </Box>
        );

        return tooltip ? (
            <Tooltip label={tooltip} withArrow>
                {boxContent}
            </Tooltip>
        ) : boxContent;
    };

    if (isLoading)
        return (
            <Box style={{ display: 'flex', justifyContent: 'center', padding: '16px' }}>
                <Loader />
            </Box>
        );
    
    if (error)
        return (
            <Alert color="red" title="Error">
                Error loading repairs scorecard data: {error.message}
            </Alert>
        );
    
    if (!data)
        return <Alert color="blue" title="No Data">No repairs scorecard data available</Alert>;

    const metrics = [
        {
            title: 'Planned Repairs Spend',
            goalDetails: [
                'Goal',
                'Margin %: 23.4%',
            ],
            rows: [
                {
                    label: 'Spend',
                    boxes: [
                        {
                            title: 'Month to Date',
                            value: data.inventoryPlannedSpendMTD,
                            status: 'gray',
                            format: 'currency',
                            mode: 'MTD'
                        },
                        {
                            title: 'Qtr to Date',
                            value: data.inventoryPlannedSpendQTD,
                            status: 'gray',
                            format: 'currency',
                            mode: 'QTD'
                        },
                        {
                            title: 'Year to Date',
                            value: data.inventoryPlannedSpendYTD,
                            status: 'gray',
                            format: 'currency',
                            mode: 'YTD'
                        }
                    ]
                },
                {
                    label: 'Projected Margin %',
                    boxes: [
                        {
                            title: 'Month to Date',
                            value: data.inventoryPlannedSpendProjectedMarginPercentMTD,
                            status: getMarginPercentStatus(data.inventoryPlannedSpendProjectedMarginPercentMTD),
                            format: 'percent',
                            mode: 'MTD'
                        },
                        {
                            title: 'Qtr to Date',
                            value: data.inventoryPlannedSpendProjectedMarginPercentQTD,
                            status: getMarginPercentStatus(data.inventoryPlannedSpendProjectedMarginPercentQTD),
                            format: 'percent',
                            mode: 'QTD'
                        },
                        {
                            title: 'Year to Date',
                            value: data.inventoryPlannedSpendProjectedMarginPercentYTD,
                            status: getMarginPercentStatus(data.inventoryPlannedSpendProjectedMarginPercentYTD),
                            format: 'percent',
                            mode: 'YTD'
                        }
                    ]
                },
                {
                    label: 'Projected Margin $',
                    boxes: [
                        {
                            title: 'Month to Date',
                            value: data.inventoryPlannedSpendProjectedMarginDollarsMTD,
                            status: 'gray',
                            format: 'currency',
                            mode: 'MTD'
                        },
                        {
                            title: 'Qtr to Date',
                            value: data.inventoryPlannedSpendProjectedMarginDollarsQTD,
                            status: 'gray',
                            format: 'currency',
                            mode: 'QTD'
                        },
                        {
                            title: 'Year to Date',
                            value: data.inventoryPlannedSpendProjectedMarginDollarsYTD,
                            status: 'gray',
                            format: 'currency',
                            mode: 'YTD'
                        }
                    ]
                }
            ],
            landed: {
                title: 'Landed Repairs Spend',
                goalDetails: [
                    'Annual Goal',
                    'Spend Goal: $10.95M',
                    'Margin %: 23.4%',
                    'Margin $: $300K'
                ],
                rows: [
                    {
                        label: 'Spend',
                        boxes: [
                            {
                                title: 'Month to Date',
                                value: data.inventoryLandedSpendMTD,
                                status: getStatusColor(data.inventoryLandedSpendMTD, 912500, true, 0.75),
                                format: 'currency',
                                mode: 'MTD'
                            },
                            {
                                title: 'Qtr to Date',
                                value: data.inventoryLandedSpendQTD,
                                status: getStatusColor(data.inventoryLandedSpendQTD, 2737500, true, 0.75),
                                format: 'currency',
                                mode: 'QTD'
                            },
                            {
                                title: 'Year to Date',
                                value: data.inventoryLandedSpendYTD,
                                status: getStatusColor(data.inventoryLandedSpendYTD, 10950000, true, 0.75),
                                format: 'currency',
                                mode: 'YTD'
                            }
                        ]
                    },
                    {
                        label: 'Projected Margin %',
                        boxes: [
                            {
                                title: 'Month to Date',
                                value: data.inventoryLandedSpendProjectedMarginPercentMTD,
                                status: getMarginPercentStatus(data.inventoryLandedSpendProjectedMarginPercentMTD),
                                format: 'percent',
                                mode: 'MTD'
                            },
                            {
                                title: 'Qtr to Date',
                                value: data.inventoryLandedSpendProjectedMarginPercentQTD,
                                status: getMarginPercentStatus(data.inventoryLandedSpendProjectedMarginPercentQTD),
                                format: 'percent',
                                mode: 'QTD'
                            },
                            {
                                title: 'Year to Date',
                                value: data.inventoryLandedSpendProjectedMarginPercentYTD,
                                status: getMarginPercentStatus(data.inventoryLandedSpendProjectedMarginPercentYTD),
                                format: 'percent',
                                mode: 'YTD'
                            }
                        ]
                    },
                    {
                        label: 'Projected Margin $',
                        boxes: [
                            {
                                title: 'Month to Date',
                                value: data.inventoryLandedSpendProjectedMarginDollarsMTD,
                                status: getStatusColor(data.inventoryLandedSpendProjectedMarginDollarsMTD, 300000, true, 0.75),
                                format: 'currency',
                                mode: 'MTD'
                            },
                            {
                                title: 'Qtr to Date',
                                value: data.inventoryLandedSpendProjectedMarginDollarsQTD,
                                status: getStatusColor(data.inventoryLandedSpendProjectedMarginDollarsQTD, 900000, true, 0.75),
                                format: 'currency',
                                mode: 'QTD'
                            },
                            {
                                title: 'Year to Date',
                                value: data.inventoryLandedSpendProjectedMarginDollarsYTD,
                                status: getStatusColor(data.inventoryLandedSpendProjectedMarginDollarsYTD, 3600000, true, 0.75),
                                format: 'currency',
                                mode: 'YTD'
                            }
                        ]
                    }
                ]
            }
        },
        {
            title: 'Revenue/Margin',
            goalDetails: [
                'Monthly Goal',
                'Rev: $1.1M',
                'Margin %: 23.4%',
                'Margin $: $300K'
            ],
            rows: [
                {
                    label: 'Revenue',
                    boxes: [
                        {
                            title: 'Month to Date',
                            value: data.revenueMTD,
                            status: getStatusColor(data.revenueMTD, 1100000, true, 0.75),
                            format: 'currency',
                            mode: 'MTD'
                        },
                        {
                            title: 'Qtr to Date',
                            value: data.revenueQTD,
                            status: getStatusColor(data.revenueQTD, 3300000, true, 0.75),
                            format: 'currency',
                            mode: 'QTD'
                        },
                        {
                            title: 'Year to Date',
                            value: data.revenueYTD,
                            status: getStatusColor(data.revenueYTD, 13200000, true, 0.75),
                            format: 'currency',
                            mode: 'YTD'
                        }
                    ]
                },
                {
                    label: 'Gross Margin %',
                    boxes: [
                        {
                            title: 'Month to Date',
                            value: data.revenueGrossMarginPercentMTD,
                            status: getMarginPercentStatus(data.revenueGrossMarginPercentMTD),
                            format: 'percent',
                            mode: 'MTD'
                        },
                        {
                            title: 'Qtr to Date',
                            value: data.revenueGrossMarginPercentQTD,
                            status: getMarginPercentStatus(data.revenueGrossMarginPercentQTD),
                            format: 'percent',
                            mode: 'QTD'
                        },
                        {
                            title: 'Year to Date',
                            value: data.revenueGrossMarginPercentYTD,
                            status: getMarginPercentStatus(data.revenueGrossMarginPercentYTD),
                            format: 'percent',
                            mode: 'YTD'
                        }
                    ]
                },
                {
                    label: 'Gross Margin $',
                    boxes: [
                        {
                            title: 'Month to Date',
                            value: data.revenueGrossMarginDollarsMTD,
                            status: getStatusColor(data.revenueGrossMarginDollarsMTD, 300000, true, 0.75),
                            format: 'currency',
                            mode: 'MTD'
                        },
                        {
                            title: 'Qtr to Date',
                            value: data.revenueGrossMarginDollarsQTD,
                            status: getStatusColor(data.revenueGrossMarginDollarsQTD, 900000, true, 0.75),
                            format: 'currency',
                            mode: 'QTD'
                        },
                        {
                            title: 'Year to Date',
                            value: data.revenueGrossMarginDollarsYTD,
                            status: getStatusColor(data.revenueGrossMarginDollarsYTD, 3600000, true, 0.75),
                            format: 'currency',
                            mode: 'YTD'
                        }
                    ]
                }
            ]
        },
        {
            title: 'Inventory Health',
            goalDetails: [
                '0-90 56%',
                '91-180 26%',
                '181-365 16%',
                '366+ 2%'
            ],
            rows: [
                {
                    label: '0-90',
                    boxes: [
                        {
                            title: 'Current',
                            value: data.inventoryHealthPercent90,
                            status: getInventoryHealthStatus(data.inventoryHealthPercent90, '0-90'),
                            format: 'percent'
                        }
                    ]
                },
                {
                    label: '91-180',
                    boxes: [
                        {
                            title: 'Current',
                            value: data.inventoryHealthPercent180,
                            status: getInventoryHealthStatus(data.inventoryHealthPercent180, '91-180'),
                            format: 'percent'
                        }
                    ]
                },
                {
                    label: '181-365',
                    boxes: [
                        {
                            title: 'Current',
                            value: data.inventoryHealthPercent365,
                            status: getInventoryHealthStatus(data.inventoryHealthPercent365, '181-365'),
                            format: 'percent'
                        }
                    ]
                },
                {
                    label: '366+',
                    boxes: [
                        {
                            title: 'Current',
                            value: data.inventoryHealthPercent365Plus,
                            status: getInventoryHealthStatus(data.inventoryHealthPercent365Plus, '366+'),
                            format: 'percent'
                        }
                    ]
                }
            ]
        }
    ];

    return (
        <Box style={{ padding: '4px', borderRadius: '8px', width: '100%', position: 'relative' }}>
            {metrics.map((metric, metricIndex) => (
                <Box
                    key={metric.title}
                    style={{
                        marginBottom: '8px',
                        padding: '6px',
                        borderRadius: '4px',
                        border: '1px solid var(--mantine-color-gray-3)',
                        position: 'relative'
                    }}
                >
                    {metricIndex === 0 ? (
                        <Box style={{ display: 'flex' }}>
                            {/* Left side - Planned Repairs */}
                            <Box style={{ width: '50%' }}>
                                <Box style={{ display: 'flex', marginBottom: '6px' }}>
                                    <Box style={{ width: '150px' }}>
                                        <Text size="md" fw={500}>
                                            {metric.title}
                                        </Text>
                                        {metric.goalDetails.map((detail, index) => (
                                            <Text key={index} size="xs" c="dimmed" style={{ display: 'block' }}>
                                                {detail}
                                            </Text>
                                        ))}
                                    </Box>

                                    <Box style={{ flex: 1 }}>
                                        {metric.rows.map((row, rowIndex) => (
                                            <Box
                                                key={rowIndex}
                                                style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: '140px 120px 120px 120px',
                                                    gap: '6px',
                                                    marginBottom: '4px',
                                                    alignItems: 'center'
                                                }}
                                            >
                                                <Text size="sm" fw={500} style={{ textAlign: 'right', paddingRight: '6px' }}>
                                                    {row.label}
                                                </Text>
                                                {row.boxes.map((box, index) => (
                                                    <CompactMetricBox
                                                        key={index}
                                                        value={box.value}
                                                        status={box.status}
                                                        format={box.format}
                                                        tooltip={box.tooltip}
                                                        onClick={box.mode ? () => handleInventoryPlannedSpendClick(box.mode) : undefined}
                                                    />
                                                ))}
                                            </Box>
                                        ))}

                                        <Box
                                            style={{
                                                display: 'grid',
                                                gridTemplateColumns: '140px 120px 120px 120px',
                                                gap: '6px',
                                                marginTop: '2px'
                                            }}
                                        >
                                            <Box />
                                            <Text size="xs" c="dimmed" ta="center">Month to Date</Text>
                                            <Text size="xs" c="dimmed" ta="center">Qtr to Date</Text>
                                            <Text size="xs" c="dimmed" ta="center">Year to Date</Text>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>

                            {/* Right side - Landed Repairs */}
                            {metric.landed && (
                                <Box style={{ width: '50%' }}>
                                    <Box style={{ display: 'flex', marginBottom: '6px' }}>
                                        <Box style={{ width: '150px' }}>
                                            <Text size="md" fw={500}>
                                                {metric.landed.title}
                                            </Text>
                                            {metric.landed.goalDetails.map((detail, index) => (
                                                <Text key={index} size="xs" c="dimmed" style={{ display: 'block' }}>
                                                    {detail}
                                                </Text>
                                            ))}
                                        </Box>

                                        <Box style={{ flex: 1 }}>
                                            {metric.landed.rows.map((row, rowIndex) => (
                                                <Box
                                                    key={rowIndex}
                                                    style={{
                                                        display: 'grid',
                                                        gridTemplateColumns: '140px 120px 120px 120px',
                                                        gap: '6px',
                                                        marginBottom: '4px',
                                                        alignItems: 'center'
                                                    }}
                                                >
                                                    <Text size="sm" fw={500} style={{ textAlign: 'right', paddingRight: '6px' }}>
                                                        {row.label}
                                                    </Text>
                                                    {row.boxes.map((box, index) => (
                                                        <CompactMetricBox
                                                            key={index}
                                                            value={box.value}
                                                            status={box.status}
                                                            format={box.format}
                                                            onClick={box.mode ? () => handleInventoryLandedSpendClick(box.mode) : undefined}
                                                        />
                                                    ))}
                                                </Box>
                                            ))}

                                            <Box
                                                style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: '140px 120px 120px 120px',
                                                    gap: '6px',
                                                    marginTop: '2px'
                                                }}
                                            >
                                                <Box />
                                                <Text size="xs" c="dimmed" ta="center">Month to Date</Text>
                                                <Text size="xs" c="dimmed" ta="center">Qtr to Date</Text>
                                                <Text size="xs" c="dimmed" ta="center">Year to Date</Text>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    ) : (
                        // Normal rendering for other metrics
                        <Box style={{ display: 'flex', marginBottom: '6px' }}>
                            <Box style={{ width: '150px' }}>
                                <Text size="md" fw={500}>
                                    {metric.title}
                                </Text>
                                {metric.goalDetails.map((detail, index) => (
                                    <Text key={index} size="xs" c="dimmed" style={{ display: 'block' }}>
                                        {detail}
                                    </Text>
                                ))}
                            </Box>

                            <Box style={{ flex: 1 }}>
                                {metric.rows.map((row, rowIndex) => (
                                    <Box
                                        key={rowIndex}
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: '140px 120px 120px 120px',
                                            gap: '6px',
                                            marginBottom: '4px',
                                            alignItems: 'center'
                                        }}
                                    >
                                        <Text size="sm" fw={500} style={{ textAlign: 'right', paddingRight: '6px' }}>
                                            {row.label}
                                        </Text>
                                        {row.boxes.map((box, index) => (
                                            <CompactMetricBox
                                                key={index}
                                                value={box.value}
                                                status={box.status}
                                                format={box.format}
                                                onClick={
                                                    (metric.title === 'Revenue/Margin' && box.mode)
                                                        ? () => handleInventoryRevenueClick(box.mode)
                                                        : (metric.title === 'Inventory Health')
                                                            ? () => handleInventoryHealthClick('current')
                                                            : (metric.title === 'New Vendor Base Growth' && row.label === '# of RO Actual' && box.mode)
                                                                ? () => handleNewVendorROClick(box.mode)
                                                                : (metric.title === 'New Vendor Base Growth' && row.label === '# of RFQs' && box.mode)
                                                                    ? () => handleNewVendorRFQClick(box.mode)
                                                                    : undefined
                                                }
                                            />
                                        ))}
                                    </Box>
                                ))}

                                {metric.rows && metric.rows.length > 0 && metric.rows[0].boxes && metric.rows[0].boxes.length > 1 && (
                                    <Box
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: '140px 120px 120px 120px',
                                            gap: '6px',
                                            marginTop: '2px'
                                        }}
                                    >
                                        <Box />
                                        <Text size="xs" c="dimmed" ta="center">Month to Date</Text>
                                        <Text size="xs" c="dimmed" ta="center">Qtr to Date</Text>
                                        <Text size="xs" c="dimmed" ta="center">Year to Date</Text>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    )}
                </Box>
            ))}
        </Box>
    );
};

export default RepairsDepartmentPerformancePanel;